import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Stack,
  Box,
  Typography,
  Divider,
  IconButton,
  Alert,
  Chip,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CustomDatePicker from "../../../utils/DatePicker/CustomDatePicker";
import {
  formatDateLocal,
  formatDateWithYear,
} from "../../../../utils/hooks/dataHandler";
import CustomDialog from "../../../utils/dialog/customDialog";

const RentalSettingsDialog = ({
  open,
  onClose,
  includeAddress,
  setIncludeAddress,
  address,
  setAddress,
  hasVacantPeriod,
  setHasVacantPeriod,
  rentalPeriods,
  setRentalPeriods,
  vacantPeriods,
  setVacantPeriods,
  calculateDays,
  financialYearStart,
  financialYearEnd,
}) => {
  // Validation state for rental periods
  const [rentalPeriodErrors, setRentalPeriodErrors] = React.useState([]);

  // State for confirmation dialog
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [deleteRentalIndex, setDeleteRentalIndex] = React.useState(null);

  // Store initial state for restoration on cancel
  const [initialState, setInitialState] = React.useState(null);

  // Store initial state when dialog opens
  React.useEffect(() => {
    if (open && !initialState) {
      setInitialState({
        includeAddress,
        address: { ...address },
        hasVacantPeriod,
        rentalPeriods: rentalPeriods ? [...rentalPeriods] : [],
        vacantPeriods: vacantPeriods ? [...vacantPeriods] : [],
        rentalPeriodErrors: [...rentalPeriodErrors],
      });
    }
  }, [
    open,
    initialState,
    includeAddress,
    address,
    hasVacantPeriod,
    rentalPeriods,
    vacantPeriods,
    rentalPeriodErrors,
  ]);

  const addRentalPeriod = () => {
    setRentalPeriods((prevRentalPeriods) => [
      ...prevRentalPeriods,
      {
        startDate: null,
        endDate: null,
        days: 0,
      },
    ]);
    setRentalPeriodErrors((prev) => [...prev, ""]);
  };

  const setBasisPeriod = () => {
    if (financialYearStart && financialYearEnd) {
      const startDate = formatDateLocal(new Date(financialYearStart));
      const endDate = formatDateLocal(new Date(financialYearEnd));
      const days = calculateDays(startDate, endDate);

      setRentalPeriods([
        {
          startDate: startDate,
          endDate: endDate,
          days: days,
        },
      ]);
      setRentalPeriodErrors([""]);
    }
  };

  const removeRentalPeriod = (index) => {
    // If there are vacant periods, prompt for confirmation
    if (vacantPeriods && vacantPeriods.length > 0) {
      setDeleteRentalIndex(index);
      setConfirmDeleteOpen(true);
    } else {
      setRentalPeriods(rentalPeriods.filter((_, i) => i !== index));
      setRentalPeriodErrors(rentalPeriodErrors.filter((_, i) => i !== index));
    }
  };

  // Handler for confirming delete (removes both rental and vacant periods)
  const handleConfirmDelete = () => {
    if (deleteRentalIndex !== null) {
      setRentalPeriods(rentalPeriods.filter((_, i) => i !== deleteRentalIndex));
      setRentalPeriodErrors(
        rentalPeriodErrors.filter((_, i) => i !== deleteRentalIndex)
      );
      setVacantPeriods([]);
      setDeleteRentalIndex(null);
      setConfirmDeleteOpen(false);
    }
  };

  // Handler for cancelling delete
  const handleCancelDelete = () => {
    setDeleteRentalIndex(null);
    setConfirmDeleteOpen(false);
  };

  // Handler for cancel button - restore initial state
  const handleCancel = () => {
    if (initialState) {
      setIncludeAddress(initialState.includeAddress);
      setAddress(initialState.address);
      setHasVacantPeriod(initialState.hasVacantPeriod);
      setRentalPeriods(initialState.rentalPeriods);
      setVacantPeriods(initialState.vacantPeriods);
      setRentalPeriodErrors(initialState.rentalPeriodErrors);
    }
    setInitialState(null); // Reset initial state
    onClose();
  };

  const updateRentalPeriod = (index, field, value) => {
    const updatedPeriods = [...rentalPeriods];
    updatedPeriods[index] = { ...updatedPeriods[index], [field]: value };

    // Recalculate days if both dates are set
    if (field === "startDate" && updatedPeriods[index].endDate) {
      updatedPeriods[index].days = calculateDays(
        value,
        updatedPeriods[index].endDate
      );
    } else if (field === "endDate" && updatedPeriods[index].startDate) {
      updatedPeriods[index].days = calculateDays(
        updatedPeriods[index].startDate,
        value
      );
    }

    // Validation
    let error = "";
    const { startDate, endDate } = updatedPeriods[index];
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      error = "End date cannot be before start date";
    }
    const errors = [...rentalPeriodErrors];
    errors[index] = error;
    setRentalPeriodErrors(errors);

    setRentalPeriods(updatedPeriods);
  };

  const addVacantPeriod = () => {
    setVacantPeriods((prevVacantPeriods) => [
      ...prevVacantPeriods,
      {
        startDate: null,
        endDate: null,
        days: 0,
      },
    ]);
  };

  const removeVacantPeriod = (index) => {
    setVacantPeriods(vacantPeriods.filter((_, i) => i !== index));
  };

  const moveVacantPeriod = (index, direction) => {
    if (direction === "up" && index > 0) {
      const newVacantPeriods = [...vacantPeriods];
      [newVacantPeriods[index], newVacantPeriods[index - 1]] = [
        newVacantPeriods[index - 1],
        newVacantPeriods[index],
      ];
      setVacantPeriods(newVacantPeriods);
    } else if (direction === "down" && index < vacantPeriods.length - 1) {
      const newVacantPeriods = [...vacantPeriods];
      [newVacantPeriods[index], newVacantPeriods[index + 1]] = [
        newVacantPeriods[index + 1],
        newVacantPeriods[index],
      ];
      setVacantPeriods(newVacantPeriods);
    }
  };

  const updateVacantPeriod = (index, field, value) => {
    const updatedPeriods = [...vacantPeriods];
    updatedPeriods[index] = { ...updatedPeriods[index], [field]: value };

    // Recalculate days if both dates are set
    if (field === "startDate" && updatedPeriods[index].endDate) {
      updatedPeriods[index].days = calculateDays(
        value,
        updatedPeriods[index].endDate
      );
    } else if (field === "endDate" && updatedPeriods[index].startDate) {
      updatedPeriods[index].days = calculateDays(
        updatedPeriods[index].startDate,
        value
      );
    }

    setVacantPeriods(updatedPeriods);
  };

  // Helper function to validate vacant period dates
  const getVacantPeriodError = (period, index) => {
    const { startDate, endDate } = period;

    // Check if end date is before start date
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      return "End date cannot be before start date";
    }

    // Check if vacant period is within rental period (if only one rental period exists)
    if (rentalPeriods && rentalPeriods.length === 1 && startDate && endDate) {
      const rental = rentalPeriods[0];
      if (
        new Date(startDate) < new Date(rental.startDate) ||
        new Date(endDate) > new Date(rental.endDate)
      ) {
        return "Vacant period must be within the rental period";
      }
    }

    return "";
  };

  // Check if any errors exist
  const hasErrors =
    rentalPeriodErrors.some(Boolean) ||
    vacantPeriods.some(
      (period, index) => !!getVacantPeriodError(period, index)
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box display='flex' alignItems='center' gap={1}>
            <SettingsIcon color='primary' />
            <Typography variant='h6' fontWeight={600}>
              Rental Schedule Settings
            </Typography>
          </Box>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Alert severity='info' sx={{ borderRadius: 2 }}>
            <Typography variant='body2'>
              Configure the property address, rental periods, and vacant periods
              for this rental schedule. These settings affect how rental income
              and expenses are calculated.
            </Typography>
          </Alert>

          <Divider />

          {/* Address Section */}
          <Box>
            <Box display='flex' alignItems='center' gap={1} mb={1}>
              <HomeWorkOutlinedIcon color='primary' fontSize='small' />
              <Typography variant='subtitle1' fontWeight={600}>
                Property Address
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={includeAddress}
                  onChange={(e) => {
                    setIncludeAddress(e.target.checked);
                    if (!e.target.checked) {
                      setAddress({
                        address_line1: null,
                        address_line2: null,
                        address_line3: null,
                      });
                    }
                  }}
                />
              }
              label='Include Address'
              sx={{
                ml: 0,
                mb: 2,
                "& .MuiFormControlLabel-label": {
                  marginLeft: "8px", // adjusts space between switch and label
                },
              }}
            />
            {includeAddress && (
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label='Address Line 1'
                  value={address.address_line1 || ""}
                  onChange={(e) =>
                    setAddress({ ...address, address_line1: e.target.value })
                  }
                />
                <TextField
                  fullWidth
                  label='Address Line 2'
                  value={address.address_line2 || ""}
                  onChange={(e) =>
                    setAddress({ ...address, address_line2: e.target.value })
                  }
                />
                <TextField
                  fullWidth
                  label='Address Line 3'
                  value={address.address_line3 || ""}
                  onChange={(e) =>
                    setAddress({ ...address, address_line3: e.target.value })
                  }
                />
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Rental Periods Section */}
          <Box>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              mb={1}
            >
              <Box display='flex' alignItems='center' gap={1}>
                <CalendarMonthIcon color='primary' fontSize='small' />
                <Typography variant='subtitle1' fontWeight={600}>
                  Rental Period
                </Typography>
              </Box>
              {(!rentalPeriods || rentalPeriods.length < 1) && (
                <Box display='flex' gap={1}>
                  <Button
                    onClick={setBasisPeriod}
                    size='small'
                    variant='contained'
                    disabled={!financialYearStart || !financialYearEnd}
                  >
                    Same as Basis Period
                  </Button>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addRentalPeriod}
                    size='small'
                    variant='outlined'
                  >
                    Add Period
                  </Button>
                </Box>
              )}
            </Box>

            {!rentalPeriods || rentalPeriods.length === 0 ? (
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                No rental periods defined. Click "Add Period" to add one.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {rentalPeriods.map((period, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                      mb={2}
                    >
                      <Chip
                        label={`Period ${index + 1}`}
                        size='small'
                        color='primary'
                      />
                      <IconButton
                        onClick={() => removeRentalPeriod(index)}
                        size='small'
                        color='error'
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Stack direction='row' spacing={2}>
                      <Box sx={{ flex: 1 }}>
                        <CustomDatePicker
                          selected={
                            period.startDate ? new Date(period.startDate) : null
                          }
                          onChange={(date) => {
                            const newStartDate = formatDateLocal(date);
                            updateRentalPeriod(
                              index,
                              "startDate",
                              newStartDate
                            );
                          }}
                          label='Rental Start Date'
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <CustomDatePicker
                          selected={
                            period.endDate ? new Date(period.endDate) : null
                          }
                          onChange={(date) => {
                            const newEndDate = formatDateLocal(date);
                            updateRentalPeriod(index, "endDate", newEndDate);
                          }}
                          label='Rental End Date'
                          fullWidth
                        />
                      </Box>
                    </Stack>
                    {rentalPeriodErrors[index] && (
                      <Typography color='error' variant='caption'>
                        {rentalPeriodErrors[index]}
                      </Typography>
                    )}
                    {period.days > 0 && (
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mt: 1 }}
                      >
                        Duration: {period.days} days
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Vacant Periods Section */}
          <Box>
            <Box display='flex' alignItems='center' gap={1} mb={1}>
              <EventAvailableIcon color='primary' fontSize='small' />
              <Typography variant='subtitle1' fontWeight={600}>
                Vacant Periods
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={hasVacantPeriod}
                  onChange={(e) => setHasVacantPeriod(e.target.checked)}
                  disabled={!rentalPeriods || rentalPeriods.length === 0}
                />
              }
              label='Include Vacant Periods'
              sx={{
                ml: 0,
                mb: 2,
                "& .MuiFormControlLabel-label": {
                  marginLeft: "8px", // adjusts space between switch and label
                },
              }}
            />
            {(!rentalPeriods || rentalPeriods.length === 0) && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mb: 2 }}
              >
                <br />
                Provide a rental period before adding vacant periods.
              </Typography>
            )}
            {hasVacantPeriod && (
              <Box>
                <Box
                  display='flex'
                  alignItems='center'
                  justifyContent='space-between'
                  mb={2}
                >
                  <Typography variant='body2' color='text.secondary'>
                    Define periods when the property was vacant
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addVacantPeriod}
                    size='small'
                    variant='outlined'
                    disabled={!rentalPeriods || rentalPeriods.length === 0}
                  >
                    Add Period
                  </Button>
                </Box>

                {!vacantPeriods || vacantPeriods.length === 0 ? (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 2 }}
                  >
                    No vacant periods defined. Click "Add Period" to add one.
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {vacantPeriods.map((period, index) => (
                      <Box
                        key={index}
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 2,
                          p: 2,
                        }}
                      >
                        <Box
                          display='flex'
                          alignItems='center'
                          justifyContent='space-between'
                          mb={2}
                        >
                          <Chip
                            label={`Vacant Period ${index + 1}`}
                            size='small'
                            color='secondary'
                          />
                          <Box display='flex' alignItems='center' gap={0.5}>
                            {vacantPeriods.length > 1 && (
                              <>
                                <Tooltip title='Move up'>
                                  <span>
                                    <IconButton
                                      onClick={() =>
                                        moveVacantPeriod(index, "up")
                                      }
                                      size='small'
                                      disabled={index === 0}
                                      sx={{
                                        color: "text.secondary",
                                        "&:hover": {
                                          color: "primary.main",
                                          backgroundColor: "primary.light",
                                        },
                                        "&:disabled": {
                                          color: "text.disabled",
                                        },
                                      }}
                                    >
                                      <ArrowUpwardIcon fontSize='small' />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title='Move down'>
                                  <span>
                                    <IconButton
                                      onClick={() =>
                                        moveVacantPeriod(index, "down")
                                      }
                                      size='small'
                                      disabled={
                                        index === vacantPeriods.length - 1
                                      }
                                      sx={{
                                        color: "text.secondary",
                                        "&:hover": {
                                          color: "primary.main",
                                          backgroundColor: "primary.light",
                                        },
                                        "&:disabled": {
                                          color: "text.disabled",
                                        },
                                      }}
                                    >
                                      <ArrowDownwardIcon fontSize='small' />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip title='Delete period'>
                              <IconButton
                                onClick={() => removeVacantPeriod(index)}
                                size='small'
                                color='error'
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "error.light",
                                  },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Stack direction='row' spacing={2}>
                          <Box sx={{ flex: 1 }}>
                            <CustomDatePicker
                              selected={
                                period.startDate
                                  ? new Date(period.startDate)
                                  : null
                              }
                              onChange={(date) => {
                                const newStartDate = formatDateLocal(date);
                                updateVacantPeriod(
                                  index,
                                  "startDate",
                                  newStartDate
                                );
                              }}
                              label='Vacant Start Date'
                              fullWidth
                              error={!!getVacantPeriodError(period, index)}
                              helperText={getVacantPeriodError(period, index)}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <CustomDatePicker
                              selected={
                                period.endDate ? new Date(period.endDate) : null
                              }
                              onChange={(date) => {
                                const newEndDate = formatDateLocal(date);
                                updateVacantPeriod(
                                  index,
                                  "endDate",
                                  newEndDate
                                );
                              }}
                              label='Vacant End Date'
                              fullWidth
                              error={!!getVacantPeriodError(period, index)}
                              helperText={getVacantPeriodError(period, index)}
                            />
                          </Box>
                        </Stack>
                        {period.days > 0 && (
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{ mt: 1 }}
                          >
                            Duration: {period.days} days
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Box>

          <Divider />

          {/* How it works Section */}
          <Box>
            <Typography variant='subtitle2' fontWeight={600} gutterBottom>
              How it works:
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              • The address is used for reporting and documentation purposes.
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              • Rental periods determine the basis period for rental income. You
              can add multiple periods for different tenants or seasons.
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              • Use "Same as Basis Period" to automatically set the rental
              period to match your financial year (
              {formatDateWithYear(financialYearStart)} to{" "}
              {formatDateWithYear(financialYearEnd)}).
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              • If vacant periods are set, expenses may be apportioned
              accordingly.
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              • You can update these settings at any time; changes will affect
              calculations and reports.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleCancel} color='inherit'>
          Cancel
        </Button>
        <Button onClick={onClose} variant='contained' disabled={hasErrors}>
          Save Settings
        </Button>
      </DialogActions>
      <CustomDialog
        open={confirmDeleteOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title='Remove Rental Period?'
        description='Deleting the rental period will also remove all vacant periods. Do you want to continue?'
        type='error'
      />
    </Dialog>
  );
};

export default RentalSettingsDialog;
